import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getAssetById, updateAsset, uploadAssetAttachment, removeAssetAttachment, setAssetPrimaryImage } from '../services/assetService';
import { Spinner, Breadcrumb } from '../../../shared/components';
import { Asset, AssetOwner } from '../types/Asset';
import { getContacts, Contact } from '../../contacts';
import { listConsultations } from '../../consultations/services/consultationService';
import type { Consultation } from '../../consultations/types/Consultation';
import { formatDateTime } from '../../../shared/utils/localization';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { CatalogMediaPanel } from '../../catalog/CatalogMediaPanel';
import { AssetOwnersField } from './AssetOwnersField';
import { AssetTypeFields } from './AssetTypeFields';
import { assetTypePath, assetsListPath, emptyDetailsFor, getAssetTypeModule, serializeDetails } from '../typeCatalog';
import { AssetTypeIcon, ButtonWithIcon, SaveIcon } from './AssetIcons';
import iconStyles from './AssetIcons.module.css';
import styles from './AssetLayout.module.css';

export const AssetDetails: React.FC = () => {
  const { t } = useTranslation(['assets', 'common', 'calendar']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission(PermissionScopes.assetsUpdate);
  const [asset, setAsset] = React.useState<Asset | null>(null);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [consultations, setConsultations] = React.useState<Consultation[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [visitsLoading, setVisitsLoading] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'details' | 'visits'>(searchParams.get('tab') === 'visits' ? 'visits' : 'details');
  const [form, setForm] = React.useState({
    name: '',
    identifier: '',
    owners: [] as AssetOwner[],
    details: {} as Record<string, unknown>,
  });
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [uploadingMedia, setUploadingMedia] = React.useState(false);

  const contactNameById = React.useMemo(() => Object.fromEntries(contacts.map(contact => [contact.id, contact.name])), [contacts]);
  const module = asset ? getAssetTypeModule(asset.assetType) : null;

  React.useEffect(() => {
    if (searchParams.get('tab') === 'visits') {
      setActiveTab('visits');
    }
  }, [searchParams]);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [contactData, assetData] = await Promise.all([
          getContacts(),
          id?.trim() ? getAssetById(id) : Promise.resolve(null),
        ]);
        setContacts(contactData);
        setAsset(assetData);
        if (assetData) {
          setForm({
            name: assetData.name,
            identifier: assetData.identifier || '',
            owners: assetData.owners,
            details: { ...emptyDetailsFor(assetData.assetType), ...assetData.details },
          });
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id]);

  React.useEffect(() => {
    if (activeTab !== 'visits' || !id) return;

    const loadVisits = async () => {
      setVisitsLoading(true);
      try {
        const data = await listConsultations({ assetId: id, page: 1, pageSize: 100 });
        setConsultations(data);
      } finally {
        setVisitsLoading(false);
      }
    };

    void loadVisits();
  }, [activeTab, id]);

  const validate = () => {
    const nextErrors: { [key: string]: string } = {};
    if (!form.name.trim()) nextErrors.name = t('nameRequired', { ns: 'assets' });
    if (form.owners.length === 0) nextErrors.owners = t('contactRequired', { ns: 'assets' });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSave = async () => {
    if (!id || !asset || !validate()) return;

    setSaving(true);
    setGeneralError(null);

    try {
      const updated = await updateAsset(id, {
        name: form.name.trim(),
        identifier: form.identifier.trim() || undefined,
        owners: form.owners,
        details: serializeDetails(asset.assetType, form.details),
      });

      if (!updated) {
        setGeneralError(t('failedToSaveAssetNotFound', { ns: 'assets' }));
        return;
      }

      setAsset(updated);
      setForm({
        name: updated.name,
        identifier: updated.identifier || '',
        owners: updated.owners,
        details: { ...emptyDetailsFor(updated.assetType), ...updated.details },
      });
      setEditMode(false);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSaveAsset', { ns: 'assets' }));
    } finally {
      setSaving(false);
    }
  };

  const applyMediaUpdate = (updated: Asset | null, fallback: string) => {
    if (!updated) {
      setGeneralError(fallback);
      return;
    }
    setAsset(updated);
  };

  const onUploadAttachment = async (file: File, setAsPrimary: boolean) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await uploadAssetAttachment(id, file, setAsPrimary);
      applyMediaUpdate(updated, t('failedToUploadAttachment', { ns: 'assets' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToUploadAttachment', { ns: 'assets' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  const onRemoveAttachment = async (attachmentId: string) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await removeAssetAttachment(id, attachmentId);
      applyMediaUpdate(updated, t('failedToRemoveAttachment', { ns: 'assets' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToRemoveAttachment', { ns: 'assets' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  const onSetPrimaryImage = async (attachmentId: string) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await setAssetPrimaryImage(id, attachmentId);
      applyMediaUpdate(updated, t('failedToSetDefaultPhoto', { ns: 'assets' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSetDefaultPhoto', { ns: 'assets' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingAsset', { ns: 'assets' })} /></div>;
  }

  if (!asset || !module) {
    return <div>{t('assetNotFound', { ns: 'assets' })}</div>;
  }

  const listPath = assetsListPath(asset.assetType);

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t(module.labelKey, { ns: 'assets' }), path: listPath },
        { label: asset.name, path: assetTypePath(asset.assetType, `/${asset.id}`) },
      ]} />

      <div className={styles.headerRow}>
        <h2 className={styles.title}>{asset.name}</h2>
        <div className={styles.actions}>
          {!editMode ? (
            <button type="button" className="btn btn-secondary" onClick={() => setEditMode(true)}>{t('edit', { ns: 'common' })}</button>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setEditMode(false)} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
              <button type="button" className={`btn btn-primary ${iconStyles.buttonWithIcon}`} onClick={onSave} disabled={saving}>
                <ButtonWithIcon icon={<SaveIcon />}>{t('save', { ns: 'assets' })}</ButtonWithIcon>
              </button>
            </>
          )}
        </div>
      </div>

      {generalError ? <div className="error">{generalError}</div> : null}

      <div className={styles.tabs}>
        <button type="button" className={activeTab === 'details' ? styles.tabButtonActive : styles.tabButton} onClick={() => setActiveTab('details')}>
          {t('detailsTitle', { ns: 'assets' })}
        </button>
        <button type="button" className={activeTab === 'visits' ? styles.tabButtonActive : styles.tabButton} onClick={() => setActiveTab('visits')}>
          {t('appointment', { ns: 'assets', defaultValue: 'Visits' })}
        </button>
      </div>

      {activeTab === 'details' ? (
        <>
          <CatalogMediaPanel
            ns="assets"
            itemName={asset.name}
            attachments={asset.attachments}
            canUpdate={canUpdate}
            busy={saving || uploadingMedia}
            onUpload={onUploadAttachment}
            onRemove={onRemoveAttachment}
            onSetPrimary={onSetPrimaryImage}
          />
        {editMode ? (
          <div className={styles.form}>
            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label className={styles.label}>{t('name', { ns: 'assets' })}</label>
                <input className={styles.input} value={form.name} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
                {errors.name ? <div className={styles.fieldError}>{errors.name}</div> : null}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>{t(module.identifierLabelKey, { ns: 'assets' })}</label>
                <input className={styles.input} value={form.identifier} onChange={event => setForm(prev => ({ ...prev, identifier: event.target.value }))} />
              </div>
            </div>
            <AssetTypeFields
              module={module}
              values={form.details}
              onChange={details => setForm(prev => ({ ...prev, details }))}
            />
            <AssetOwnersField
              contacts={contacts}
              owners={form.owners}
              onChange={owners => setForm(prev => ({ ...prev, owners }))}
              error={errors.owners}
            />
          </div>
        ) : (
          <>
            <div className={styles.detailsGrid}>
              <div>
                <p className={styles.detailLabel}>{t('type', { ns: 'assets' })}</p>
                <p className={styles.detailValue}>
                  <span className={iconStyles.typeTabLabel}>
                    <AssetTypeIcon type={asset.assetType} />
                    {t(module.labelKey, { ns: 'assets' })}
                  </span>
                </p>
              </div>
              <div>
                <p className={styles.detailLabel}>{t(module.identifierLabelKey, { ns: 'assets' })}</p>
                <p className={styles.detailValue}>{asset.identifier || '-'}</p>
              </div>
              <div className={styles.detailItemWide}>
                <p className={styles.detailLabel}>{t('owners', { ns: 'assets' })}</p>
                <p className={styles.detailValue}>
                  {asset.owners.length > 0
                    ? asset.owners.map(owner => {
                        const name = contactNameById[owner.contactPersonId] || owner.contactPersonId;
                        const role = owner.isRegistered
                          ? t('registeredOwner', { ns: 'assets' })
                          : t('unregisteredOwner', { ns: 'assets' });
                        const primary = owner.isPrimary ? ` · ${t('primaryOwner', { ns: 'assets' })}` : '';
                        return `${name} (${role}${primary})`;
                      }).join(', ')
                    : '-'}
                </p>
              </div>
            </div>
            <div style={{ marginTop: 24 }}>
              <AssetTypeFields module={module} values={asset.details} onChange={() => undefined} readOnly />
            </div>
          </>
        )
        }
        </>
      ) : visitsLoading ? (
        <Spinner text={t('loadingAsset', { ns: 'assets' })} />
      ) : consultations.length === 0 ? (
        <div className={styles.emptyState}>{t('noNotesYet', { ns: 'assets', defaultValue: 'No visits yet' })}</div>
      ) : (
        <div className={styles.visitsTableWrap}>
          <table className={styles.visitsTable}>
            <thead>
              <tr>
                <th>{t('date', { ns: 'common' })}</th>
                <th>{t('appointment', { ns: 'assets' })}</th>
                <th>{t('notes', { ns: 'calendar' })}</th>
              </tr>
            </thead>
            <tbody>
              {consultations.map(consultation => (
                <tr
                  key={consultation.id}
                  className={styles.clickableRow}
                  onClick={() => navigate(`/consultations/${consultation.id}`)}
                >
                  <td>{formatDateTime(consultation.updatedAt || consultation.createdAt)}</td>
                  <td>{consultation.eventId || '-'}</td>
                  <td>{consultation.notes || t('noNotesYet', { ns: 'assets' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(listPath)}>{t('backToList', { ns: 'assets' })}</button>
      </div>
    </div>
  );
};

export default AssetDetails;
